import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Typography, Paper, Grid } from '@mui/material';
import * as eqpMagazineApi from '../../lib/api/eqpMagazine';
import * as eqpToolLifeApi from '../../lib/api/eqpToolLife';
import { formatHours, buildToolByNo } from './eqpToolLifeFormat';
import EqpMagazinePotPanel from './EqpMagazinePotPanel';
import EqpToolLifeTable from './EqpToolLifeTable';
import EqpToolTimeline from './EqpToolTimeline';

const REFRESH_MS = 15000;

const SummaryStat = ({ value, label, color }) => (
  <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center', height: '100%' }}>
    <Typography variant="h5" sx={{ fontWeight: 700 }} color={color || 'text.primary'}>{value}</Typography>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
  </Paper>
);

/**
 * 매거진 현황 탭 - 공구 사용·교체 시기(2026-10-19 개편).
 * 요약(가동시간/교환횟수/임박/초과) + 좌(주축 공구·포트 그리드) + 우(공구별 사용·교체 표) + 하단 타임라인.
 * usage/magazine 은 열려있는 동안 15초마다 새로고침(저장은 EqpToolLifeTable 의 다이얼로그가 처리하고
 * 성공하면 이 탭에 즉시 재조회를 부탁한다). 타임라인은 자체적으로 60초 주기로 따로 돈다.
 */
const EqpMagazineTab = ({ eqpUuid, canEdit = true }) => {
  const [usage, setUsage] = useState(null);
  const [magazineView, setMagazineView] = useState(null);
  const timerRef = useRef(null);

  const load = useCallback(async () => {
    if (!eqpUuid) return;
    try {
      const [usageRes, magazineRes] = await Promise.all([
        eqpToolLifeApi.getToolUsage(eqpUuid),
        eqpMagazineApi.getMagazine(eqpUuid),
      ]);
      setUsage(eqpToolLifeApi.extractInfo(usageRes));
      setMagazineView(eqpMagazineApi.extractInfo(magazineRes));
    } catch (e) {
      // 조용히 - 다음 주기에 다시
    }
  }, [eqpUuid]);

  useEffect(() => {
    load();
    timerRef.current = setInterval(load, REFRESH_MS);
    return () => clearInterval(timerRef.current);
  }, [load]);

  const summary = usage?.summary;
  const tools = usage?.tools || [];
  const toolByNo = buildToolByNo(tools);
  const curToolRecord = tools.find((t) => t.current);

  return (
    <Box>
      <Grid container spacing={1.5} sx={{ mb: 2.5 }}>
        <Grid item xs={6} sm={3}>
          <SummaryStat value={formatHours(summary?.runMinTotal)} label="가동 시간" />
        </Grid>
        <Grid item xs={6} sm={3}>
          <SummaryStat value={summary?.changeCount ?? 0} label="공구 교환 횟수" />
        </Grid>
        <Grid item xs={6} sm={3}>
          <SummaryStat
            value={summary?.warnCount ?? 0} label="교체 임박"
            color={summary?.warnCount > 0 ? 'warning.main' : undefined}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <SummaryStat
            value={summary?.overCount ?? 0} label="기준 초과"
            color={summary?.overCount > 0 ? 'error.main' : undefined}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={5}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>매거진 포트 현황</Typography>
          <EqpMagazinePotPanel
            magazine={magazineView?.magazine}
            curTool={summary?.curTool ?? magazineView?.curTool}
            curToolSince={summary?.curToolSince ?? magazineView?.curToolSince}
            curToolUsedMin={curToolRecord?.usedMin}
            toolByNo={toolByNo}
          />
        </Grid>
        <Grid item xs={12} md={7}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>공구별 사용 · 교체 시기</Typography>
          <EqpToolLifeTable eqpUuid={eqpUuid} tools={tools} onRefresh={load} canEdit={canEdit} />
        </Grid>
      </Grid>

      <Typography variant="subtitle2" sx={{ mb: 1 }}>운전 · 공구 · 매크로 타임라인</Typography>
      <EqpToolTimeline eqpUuid={eqpUuid} />
    </Box>
  );
};

export default EqpMagazineTab;
